import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getAbilities } from 'client/api-utils';
import { WebsocketEvents } from 'common/enums/notification-service-enums';
import { useSocket } from 'client/store/notification-service/socket-context';
import { customerReset, customerSelected, getCustomers } from 'client/store/entities/customer/customer';
import { getCustomerState } from 'client/store/entities/customer/selectors';
import { resetArchives } from 'client/store/entities/hierarchy/archive/archive';
import { getUserUnitsTemplatesData } from 'client/store/entities/unit-templates/unit-templates';
import {
  getUnitTemplateConfigurationSelector, getUnitTemplateLoadingSelector,
} from 'client/store/entities/unit-templates/selectors';
import { featureFlagsInitialized } from 'client/store/entities/feature-flags/feature-flags';
import { getIPMFilesData } from 'client/store/entities/ipm-files/ipm-files';

export const useCompanyContext = (): { isReady: boolean } => {
  const { companyId } = useParams();
  const { socket, isConnected } = useSocket();
  const dispatch = useDispatch();
  const { customers, selectedCustomer } = useSelector(getCustomerState);
  const isUnitTemplateLoading = useSelector(getUnitTemplateLoadingSelector);
  const unitTemplateConfiguration = useSelector(getUnitTemplateConfigurationSelector);
  const selectedCompanyId = selectedCustomer?.companyId;

  useEffect(() => {
    dispatch(getCustomers());

    return () => {
      dispatch(customerReset());
      dispatch(resetArchives());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!companyId || selectedCompanyId === companyId) {
      return;
    }
    const customer = customers.find((customer) => customer.companyId === companyId);
    if (!customer) {
      return;
    }
    dispatch(customerSelected(customer));

    if (isConnected) {
      socket.emit(WebsocketEvents.ChangeScope, { scope: 'GROUP', value: companyId });
    }
  }, [companyId, customers, selectedCompanyId, dispatch]);

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    getAbilities().then(() => {
      dispatch(featureFlagsInitialized());
    });
    dispatch(getUserUnitsTemplatesData());
    dispatch(getIPMFilesData());
  }, [selectedCompanyId, dispatch]);

  const isCustomerReady = !!companyId && selectedCompanyId === companyId;
  const isUnitTemplateReady = !isUnitTemplateLoading || !!unitTemplateConfiguration;

  return { isReady: isCustomerReady && isUnitTemplateReady };
};
